import React, { useState } from "react";
import { backendApi } from "./api/backendApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, Download, Filter, BarChart3, Clock, TrendingUp, CheckCircle, XCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import LeaveForm from "../components/leave/LeaveForm";
import DetailModal from "./DetailModal";

const T = {
  green: '#1A3C2B', greenDeep: '#0E2118', greenTint: '#EDF3EF', greenGlow: 'rgba(26,60,43,0.1)',
  bg: '#F4F5F0', card: '#FFFFFF', line: '#E3E2DC', lineSoft: '#EEEEE9',
  ink: '#111418', body: '#3A4149', muted: '#687079', faint: '#9BA3AB',
};
const LEAVE_C = { sick: '#3B6FA6', casual: '#2F7D4F', paid: '#E0A33E' };

const MetricCard = ({ icon: Icon, value, label, color, tint, badge }) => (
  <div style={{
    background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 18,
    padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden',
  }}>
    <div style={{ position: 'absolute', bottom: 0, right: 0, width: 100, height: 100, borderRadius: '80px 0 18px 0', background: tint, opacity: 0.4 }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <div style={{ width: 40, height: 40, borderRadius: 11, background: tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={18} color={color} strokeWidth={2} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: tint, color: color }}>{badge}</span>
    </div>
    <p style={{ fontSize: 32, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.03em' }}>{value}</p>
    <p style={{ fontSize: 12, color: T.muted, margin: '4px 0 0', fontWeight: 500 }}>{label}</p>
  </div>
);

const StatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const [bg, fg] = s.includes('approve') ? ['#E8F3EC','#2F7D4F'] : s.includes('reject') ? ['#FCEAEA','#C0413D'] : ['#FBF1E1','#B9822E'];
  return <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, background: bg, color: fg }}>{status}</span>;
};

export default function Leave() {
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [hoverMonth, setHoverMonth] = useState(null);
  const queryClient = useQueryClient();

  // Approved leave days by type for the last 6 months, aggregated server-side.
  const { data: leaveRequests = [] } = useQuery({ queryKey: ['leaveRequests'], queryFn: () => backendApi.entities.LeaveRequest.list(), initialData: [] });
  const { data: monthlyData = [] } = useQuery({ queryKey: ['leave-monthly'], queryFn: () => backendApi.leave.monthlySummary(), initialData: [] });

  const createMutation = useMutation({
    mutationFn: (d) => backendApi.entities.LeaveRequest.create(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leaveRequests'] }); setShowApplyForm(false); toast.success("Leave request submitted!"); },
  });

  const handleExport = () => {
    if (!leaveRequests.length) return toast.error("No records to export");
    const csv = [["Employee","Type","Start","End","Days","Reason","Status"].join(","), ...leaveRequests.map(r => [r.employee_name,r.leave_type,r.start_date,r.end_date,r.days,r.reason,r.status].join(","))].join("\n");
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download=`leave_${format(new Date(),'yyyy-MM-dd')}.csv`; a.click();
  };

  const total = leaveRequests.length;
  const pending = leaveRequests.filter(r => r.status === "Pending").length;
  const approved = leaveRequests.filter(r => r.status === "Approved").length;
  const rejected = leaveRequests.filter(r => r.status === "Rejected").length;

  const fmtDate = (d) => { if (!d) return '—'; try { return format(new Date(d), 'dd MMM yyyy'); } catch { return d; } };

  const monthPeak = Math.max(1, ...monthlyData.map(m => (m.sick||0)+(m.casual||0)+(m.paid||0)));
  const monthTop = Math.ceil(monthPeak / 4) * 4;

  const filteredReqs = leaveRequests.filter(r => statusFilter === "All" || r.status === statusFilter);

  return (
    <div style={{ background: T.bg, minHeight: '100%', padding: 28 }}>
      <style>{`
        .leave-row:hover{background:#F7F8F5!important}
        .leave-btn{transition:all 0.15s;cursor:pointer}
        .leave-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,0.12)}
        .leave-bar{transition:height 0.5s cubic-bezier(0.16,1,0.3,1)}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 26 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.02em' }}>Leave Management</h2>
          <p style={{ fontSize: 12.5, color: T.muted, margin: '3px 0 0' }}>Track employee leaves and balances</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 10 }}>
            <Filter size={14} color={T.muted} />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ background: 'transparent', border: 'none', fontSize: 13, color: T.body, outline: 'none', cursor: 'pointer' }}>
              {['All','Pending','Approved','Rejected'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button className="leave-btn" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 7, height: 38, padding: '0 16px', background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 10, fontSize: 13, fontWeight: 600, color: T.body }}>
            <Download size={14} /> Export
          </button>
          <button className="leave-btn" onClick={() => setShowApplyForm(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, height: 38, padding: '0 18px', background: T.green, border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#fff', boxShadow: '0 2px 10px rgba(26,60,43,0.2)' }}>
            <Calendar size={14} /> Apply Leave
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showApplyForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <LeaveForm onSubmit={d => createMutation.mutate(d)} onCancel={() => setShowApplyForm(false)} isLoading={createMutation.isLoading} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
        <MetricCard icon={TrendingUp} value={total} label="Total Requests" color="#3B6FA6" tint="#EAF1FB" badge="All Time" />
        <MetricCard icon={Clock} value={pending} label="Pending Approval" color="#B9822E" tint="#FBF1E1" badge="Awaiting" />
        <MetricCard icon={CheckCircle} value={approved} label="Approved Leaves" color="#2F7D4F" tint="#E8F3EC" badge="Confirmed" />
        <MetricCard icon={XCircle} value={rejected} label="Rejected Requests" color="#C0413D" tint="#FCEAEA" badge="Declined" />
      </div>

      {/* Chart + quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 18, marginBottom: 22 }}>
        {/* Monthly bar chart */}
        <div style={{ background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 18, padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <BarChart3 size={16} color={T.green} strokeWidth={2} />
            <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>Monthly Leave Distribution</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 14 }}>
              {Object.entries(LEAVE_C).map(([k, c]) => (
                <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: T.muted }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
                  {k.charAt(0).toUpperCase() + k.slice(1)}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 180 }}>
            {monthlyData.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.faint, fontSize: 13 }}>No leave data yet</div>
            ) : monthlyData.map((m, i) => {
              const sick = m.sick || 0, casual = m.casual || 0, paid = m.paid || 0;
              const monthTotal = sick + casual + paid;
              const isHover = hoverMonth === i;
              const h = `${Math.max(monthTotal > 0 ? 6 : 2, (monthTotal / monthTop) * 100)}%`;
              return (
                <div key={m.name} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', cursor: 'default', position: 'relative' }}
                  onMouseEnter={() => setHoverMonth(i)} onMouseLeave={() => setHoverMonth(null)}>
                  {isHover && (
                    <div style={{ position: 'absolute', bottom: '100%', marginBottom: 8, background: T.ink, borderRadius: 10, padding: '8px 12px', whiteSpace: 'nowrap', zIndex: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                      <p style={{ color: '#fff', fontSize: 11.5, fontWeight: 700, margin: '0 0 4px' }}>{m.name}</p>
                      <p style={{ color: '#B9D3EE', fontSize: 11, margin: '0 0 2px' }}>Sick: {sick}</p>
                      <p style={{ color: '#B9E4C6', fontSize: 11, margin: '0 0 2px' }}>Casual: {casual}</p>
                      <p style={{ color: '#F3DFB0', fontSize: 11, margin: 0 }}>Paid: {paid}</p>
                    </div>
                  )}
                  <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 8 }}>
                    <div className="leave-bar" style={{
                      width: '60%', maxWidth: 36, height: h, borderRadius: '5px 5px 0 0',
                      overflow: 'hidden', filter: isHover ? 'brightness(1.1)' : 'none',
                      boxShadow: isHover ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
                    }}>
                      {paid > 0 && <div style={{ height: `${(paid/monthTotal)*100}%`, background: LEAVE_C.paid }} />}
                      {casual > 0 && <div style={{ height: `${(casual/monthTotal)*100}%`, background: LEAVE_C.casual }} />}
                      {sick > 0 && <div style={{ height: `${(sick/monthTotal)*100}%`, background: LEAVE_C.sick }} />}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: isHover ? 700 : 500, color: isHover ? T.ink : T.muted }}>{m.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Leave type breakdown */}
        <div style={{ background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 18, padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <p style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, margin: '0 0 16px' }}>Leave Breakdown</p>
          {[
            { label: 'Approved', value: approved, pct: total ? Math.round(approved/total*100) : 0, color: '#2F7D4F' },
            { label: 'Pending', value: pending, pct: total ? Math.round(pending/total*100) : 0, color: '#B9822E' },
            { label: 'Rejected', value: rejected, pct: total ? Math.round(rejected/total*100) : 0, color: '#C0413D' },
          ].map(({ label, value, pct, color }) => (
            <div key={label} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.body }}>{label}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink }}>{value}</span>
              </div>
              <div style={{ height: 6, background: T.lineSoft, borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1)' }} />
              </div>
              <p style={{ fontSize: 11, color: T.faint, margin: '4px 0 0' }}>{pct}% of total</p>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 18, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1.5px solid ${T.lineSoft}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Calendar size={16} color={T.green} strokeWidth={2} />
          <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>Leave Requests</span>
          {filteredReqs.length > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: T.muted }}>{filteredReqs.length} record{filteredReqs.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        {filteredReqs.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.lineSoft }}>
                  {['Employee','Leave Type','Start Date','End Date','Duration','Status',''].map(h => (
                    <th key={h} style={{ padding: '11px 18px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredReqs.map((r, i) => (
                  <tr key={r.id} className="leave-row" style={{ borderBottom: i < filteredReqs.length - 1 ? `1px solid ${T.lineSoft}` : 'none', background: T.card }}>
                    <td style={{ padding: '13px 18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 9, background: T.greenTint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: T.green }}>{r.employee_name?.[0]||'?'}</div>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{r.employee_name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '13px 18px' }}>
                      <span style={{ fontSize: 13, color: T.body, fontWeight: 500 }}>{r.leave_type}</span>
                    </td>
                    <td style={{ padding: '13px 18px', fontSize: 13, color: T.body }}>{fmtDate(r.start_date)}</td>
                    <td style={{ padding: '13px 18px', fontSize: 13, color: T.body }}>{fmtDate(r.end_date)}</td>
                    <td style={{ padding: '13px 18px' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{r.days} day{r.days !== 1 ? 's' : ''}</span>
                    </td>
                    <td style={{ padding: '13px 18px' }}><StatusPill status={r.status} /></td>
                    <td style={{ padding: '13px 18px' }}>
                      <button onClick={() => setViewing(r)} style={{
                        padding: '5px 12px', borderRadius: 8, border: `1.5px solid ${T.line}`, background: T.card,
                        fontSize: 12, fontWeight: 600, color: T.body, cursor: 'pointer', transition: 'all 0.15s',
                      }}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '52px 20px' }}>
            <Calendar size={40} color={T.faint} strokeWidth={1.5} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14, fontWeight: 700, color: T.ink, margin: '0 0 4px' }}>No leave requests</p>
            <p style={{ fontSize: 13, color: T.muted, margin: 0 }}>Click "Apply Leave" to submit a new request.</p>
          </div>
        )}
      </div>

      <DetailModal open={!!viewing} title={viewing?.employee_name || "Leave Request"} subtitle={viewing?.leave_type}
        fields={[
          { label: "Employee", value: viewing?.employee_name },
          { label: "Leave Type", value: viewing?.leave_type },
          { label: "Start Date", value: fmtDate(viewing?.start_date) },
          { label: "End Date", value: fmtDate(viewing?.end_date) },
          { label: "Days", value: viewing?.days },
          { label: "Status", value: viewing?.status },
          { label: "Reason", value: viewing?.reason },
          { label: "Approved By", value: viewing?.approved_by },
        ]}
        onClose={() => setViewing(null)} />
    </div>
  );
}